import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, Upload, FileText, AlertCircle, ShieldAlert, Copy, Check } from 'lucide-react';
import { signingApi } from '../../services/iso20022Api';
import { copyToClipboard } from '../../utils/helpers';

function RjctPanel({ xml }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyToClipboard(xml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Extract a few key fields for the summary row
  const extract = (tag) => {
    const m = xml.match(new RegExp(`<${tag}>(.*?)<\\/${tag}>`, 's'));
    return m ? m[1].trim() : '—';
  };

  return (
    <div className="card bg-orange-50 border-orange-300">
      <div className="card-body">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <ShieldAlert className="w-5 h-5 text-orange-600 shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-orange-900">
                Auto-generated pacs.002 RJCT
              </h4>
              <p className="text-xs text-orange-700 mt-0.5">
                The engine generated this signed rejection in response to the failed pacs.008.
                Forward it to the originating party.
              </p>
            </div>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-100 text-orange-800 hover:bg-orange-200 transition shrink-0"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            <span>{copied ? 'Copied' : 'Copy RJCT'}</span>
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[
            { label: 'RJCT Msg ID',   value: extract('MsgId')          },
            { label: 'Orig Msg ID',   value: extract('OrgnlMsgId')     },
            { label: 'Group Status',  value: extract('GrpSts')         },
            { label: 'Reason Code',   value: extract('Cd')             },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-lg border border-orange-200 p-2">
              <p className="text-xs text-orange-600 font-medium">{label}</p>
              <p className="text-xs font-mono text-gray-900 truncate mt-0.5">{value}</p>
            </div>
          ))}
        </div>

        {/* Reason detail */}
        <div className="bg-white rounded-lg border border-orange-200 p-2 mb-3">
          <p className="text-xs text-orange-600 font-medium mb-0.5">Additional Info</p>
          <p className="text-xs font-mono text-gray-700">{extract('AddtlInf')}</p>
        </div>

        {/* Raw XML (collapsed) */}
        <details className="text-xs">
          <summary className="cursor-pointer text-orange-700 font-medium hover:text-orange-900">
            View raw RJCT XML
          </summary>
          <pre className="mt-2 bg-white border border-orange-200 rounded-lg p-3 overflow-x-auto text-gray-700 font-mono whitespace-pre-wrap break-all">
            {xml}
          </pre>
        </details>
      </div>
    </div>
  );
}

const MessageValidation = () => {
  const [xmlInput, setXmlInput] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validateSignature = async () => {
    if (!xmlInput.trim()) {
      setError('Please provide XML content to validate');
      return;
    }

    setError(null);
    setValidationResult(null);

    try {
      setLoading(true);
      const result = await signingApi.verifyMessage(xmlInput);
      setValidationResult(result);
    } catch (err) {
      setError('Validation failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setXmlInput(e.target.result);
        setValidationResult(null);
        setError(null);
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsText(file);
    }
  };

  const clearInput = () => {
    setXmlInput('');
    setValidationResult(null);
    setError(null);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setXmlInput(text);
      setValidationResult(null);
      setError(null);
    } catch (err) {
      setError('Failed to read from clipboard');
    }
  };

  const loadExample = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<BusinessMessage xmlns="urn:iso:std:iso:20022:tech:xsd:head.001.001.03">
  <AppHdr>
    <Fr>
      <FIId>
        <FinInstnId>
          <BICFI>TESTBIC1XXX</BICFI>
        </FinInstnId>
      </FIId>
    </Fr>
    <To>
      <FIId>
        <FinInstnId>
          <BICFI>TESTBIC2XXX</BICFI>
        </FinInstnId>
      </FIId>
    </To>
    <BizMsgIdr>MSG-${Date.now()}</BizMsgIdr>
    <MsgDefIdr>pacs.008.001.10</MsgDefIdr>
    <CreDt>${new Date().toISOString()}</CreDt>
  </AppHdr>
  <Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.008.001.10">
    <FIToFICstmrCdtTrf>
      <GrpHdr>
        <MsgId>MSG-${Date.now()}</MsgId>
        <CreDtTm>${new Date().toISOString()}</CreDtTm>
        <NbOfTxs>1</NbOfTxs>
      </GrpHdr>
      <CdtTrfTxInf>
        <PmtId>
          <EndToEndId>E2E-${Date.now()}</EndToEndId>
          <TxId>TX-${Date.now()}</TxId>
        </PmtId>
        <IntrBkSttlmAmt Ccy="RWF">10000.00</IntrBkSttlmAmt>
        <Dbtr><Nm>Example Debtor</Nm></Dbtr>
        <Cdtr><Nm>Example Creditor</Nm></Cdtr>
      </CdtTrfTxInf>
    </FIToFICstmrCdtTrf>
  </Document>
  <!-- Note: This is unsigned example XML. Use Message Generator to create signed XML -->
</BusinessMessage>`;
    setXmlInput(exampleXml);
    setValidationResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Message Validation</h1>
        <p className="text-gray-600 mt-1">Verify digital signatures on ISO 20022 XML messages</p>
      </div>

      {/* Input Methods */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Input Methods</h2>
        </div>
        <div className="card-body">
          <div className="flex items-center space-x-3">
            <label className="btn-secondary cursor-pointer flex items-center space-x-2">
              <Upload className="w-4 h-4" />
              <span>Upload XML File</span>
              <input
                type="file"
                accept=".xml,text/xml,application/xml"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={handlePaste}
              className="btn-secondary flex items-center space-x-2"
            >
              <FileText className="w-4 h-4" />
              <span>Paste from Clipboard</span>
            </button>
            <button
              onClick={loadExample}
              className="btn-secondary flex items-center space-x-2"
            >
              <FileText className="w-4 h-4" />
              <span>Load Example</span>
            </button>
            {xmlInput && (
              <button
                onClick={clearInput}
                className="btn-secondary flex items-center space-x-2"
              >
                <XCircle className="w-4 h-4" />
                <span>Clear</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* XML Input */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Signed XML Message</h2>
          <div className="text-sm text-gray-500">
            {xmlInput.length > 0 && `${(xmlInput.length / 1024).toFixed(1)} KB`}
          </div>
        </div>
        <div className="card-body p-0">
          <textarea
            className="w-full h-96 p-4 font-mono text-sm border-0 focus:ring-0 resize-none"
            value={xmlInput}
            onChange={(e) => {
              setXmlInput(e.target.value);
              setValidationResult(null);
              setError(null);
            }}
            placeholder="Paste your signed XML message here or use the buttons above to load..."
          />
        </div>
      </div>

      {/* Validation Button */}
      <div className="flex items-center justify-center">
        <button
          onClick={validateSignature}
          disabled={loading || !xmlInput.trim()}
          className="btn-primary flex items-center space-x-2 px-8 py-3"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Validating Signature...</span>
            </>
          ) : (
            <>
              <Shield className="w-5 h-5" />
              <span>Validate Signature</span>
            </>
          )}
        </button>
      </div>

      {/* Validation Result */}
      {validationResult && (() => {
        const xsdErrors = (validationResult.errors || []).filter(e => e.code === 'XSD_VIOLATION');
        const sigErrors  = (validationResult.errors || []).filter(e => e.code !== 'XSD_VIOLATION');
        return (
          <div className="space-y-4">
            {/* Signature result */}
            <div className={`card ${validationResult.valid ? 'bg-success-50 border-success-200' : 'bg-danger-50 border-danger-200'}`}>
              <div className="card-body">
                <div className="flex items-start space-x-4">
                  {validationResult.valid
                    ? <CheckCircle className="w-12 h-12 text-success-600 flex-shrink-0" />
                    : <XCircle    className="w-12 h-12 text-danger-600  flex-shrink-0" />}
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-2 ${validationResult.valid ? 'text-success-900' : 'text-danger-900'}`}>
                      {validationResult.valid ? 'Signature Valid ✓' : 'Signature Invalid ✗'}
                    </h3>
                    <p className={`text-sm mb-4 ${validationResult.valid ? 'text-success-800' : 'text-danger-800'}`}>
                      {validationResult.valid
                        ? 'The digital signature is valid. The message has not been tampered with and the signature is authentic.'
                        : 'The digital signature verification failed. The message may have been modified or the signature is invalid.'}
                    </p>

                    {sigErrors.length > 0 && (
                      <div className="mt-2 p-3 rounded-lg bg-danger-100">
                        {sigErrors.map((e, i) => (
                          <p key={i} className="text-sm font-mono text-danger-800">{e.message}</p>
                        ))}
                      </div>
                    )}

                    {validationResult.valid && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-lg border border-success-200">
                          <div className="text-xs text-success-600 font-medium mb-1">Signature Algorithm</div>
                          <div className="text-sm text-gray-900 font-mono">RSA-SHA256</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-success-200">
                          <div className="text-xs text-success-600 font-medium mb-1">Canonicalization</div>
                          <div className="text-sm text-gray-900 font-mono">Exclusive C14N</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-success-200">
                          <div className="text-xs text-success-600 font-medium mb-1">Digest Method</div>
                          <div className="text-sm text-gray-900 font-mono">SHA-256</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* XSD schema violations — separate from signature result */}
            {xsdErrors.length > 0 && (
              <div className="card bg-amber-50 border-amber-200">
                <div className="card-body">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-amber-900 mb-1">
                        XSD Schema Violations ({xsdErrors.length})
                      </h4>
                      <p className="text-xs text-amber-700 mb-3">
                        The message content does not fully conform to the ISO 20022 XSD schema.
                        The signature may still be valid — these are structural/content issues.
                      </p>
                      <ul className="space-y-2">
                        {xsdErrors.map((e, i) => (
                          <li key={i} className="text-xs font-mono bg-amber-100 rounded p-2 text-amber-900 break-all">
                            {e.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* XSD passed badge — only shown when signature is also valid */}
            {validationResult.valid && xsdErrors.length === 0 && (
              <div className="card bg-green-50 border-green-200">
                <div className="card-body py-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      XSD schema validation passed — message is fully ISO 20022 conformant
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Auto-generated pacs.002 RJCT — shown when a failed pacs.008 triggers Phase 3 */}
      {validationResult?.rjctXml && (
        <RjctPanel xml={validationResult.rjctXml} />
      )}

      {/* Error Display */}
      {error && (
        <div className="card bg-danger-50 border-danger-200">
          <div className="card-body">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-danger-600 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-danger-900 mb-1">Validation Error</h4>
                <p className="text-sm text-danger-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="card-body">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">About Signature Validation</h3>
          <ul className="text-sm text-blue-800 space-y-2 list-disc list-inside">
            <li>This tool verifies the <strong>ISO 20022 BAH signature profile</strong> — a W3C XMLDSig signature carried in <code>AppHdr/Sgntr</code> (head.001.001.01)</li>
            <li>Valid signatures confirm the message hasn't been altered and comes from a trusted source</li>
            <li>The validation checks:
              <ul className="ml-6 mt-1 space-y-1 list-circle list-inside">
                <li>Complete Business Application Header (Fr, To, BizMsgIdr, MsgDefIdr, CreDt)</li>
                <li>Signature located at <code>AppHdr/Sgntr</code>, exactly one per message</li>
                <li>Profile algorithms only: exclusive C14N, SHA-256, RSA-SHA256 / ECDSA-SHA256</li>
                <li>Three references intact: AppHdr, business Document, and KeyInfo</li>
                <li>Signing certificate valid and bound to the signature (no raw key material)</li>
              </ul>
            </li>
            <li>Only signed XML messages can be validated - use <strong>Message Generator</strong> to create signed messages</li>
            <li>All corridor message types are supported (pacs, acmt, camt, pain, admi)</li>
          </ul>
        </div>
      </div>

      {/* Technical Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-sm font-semibold text-gray-900">Validation Process</h3>
          </div>
          <div className="card-body">
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Hardened parse (XXE-safe, no doctype)</li>
              <li>Structure: complete BAH; signature strictly at <code>AppHdr/Sgntr</code>; single signature (wrapping defense)</li>
              <li>Algorithm allow-list: exc-C14N, SHA-256, RSA/ECDSA-SHA256 (downgrade defense)</li>
              <li>Credential: certificate from <code>&lt;ds:KeyInfo&gt;</code>, validity window, key strength</li>
              <li>Core XMLDSig validation of all three references and the signature value</li>
              <li>Return staged result — the failing stage and reference are reported</li>
            </ol>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-sm font-semibold text-gray-900">Common Issues</h3>
          </div>
          <div className="card-body">
            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
              <li><strong>Digest Mismatch:</strong> Message content was modified after signing</li>
              <li><strong>Invalid Signature:</strong> Signature doesn't match the signing key</li>
              <li><strong>Missing Elements:</strong> Required signature elements are absent</li>
              <li><strong>Whitespace Issues:</strong> Canonicalization problems</li>
              <li><strong>Unsigned Message:</strong> No signature element found</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageValidation;
